// The fleet.
//
// This screen was a read-only list derived from the stats endpoint. The server has
// enforced released and deleted devices for some time — refusing their readings,
// fails closed, with tests — and nothing here could set either flag. An operator
// could be told a device was off while it carried on weighing.
//
// So the actions are the point of this file, and the release dialog is the part that
// needs care: it cannot be undone and it frees the hardware for another account.
//
// ─── Why the row does not wear three link buttons any more ─────────────
// It did: Rename, Disable and Release, all the same weight, all in blue, with the
// irreversible one a pixel's slip from the harmless one. Enable/Disable is the
// action somebody performs during a shift, so it stays on the row; Rename is rare
// and Release is dangerous, so both move behind the overflow, where the danger
// item is styled as one and cannot be hit by a misplaced click.
import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    App,
    Button,
    Card,
    Dropdown,
    Empty,
    Form,
    Input,
    Modal,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    ApiOutlined,
    ClockCircleOutlined,
    DisconnectOutlined,
    EditOutlined,
    EnvironmentOutlined,
    HourglassOutlined,
    MoreOutlined,
    PlusOutlined,
    ReloadOutlined,
    StopOutlined,
    UndoOutlined,
} from '@ant-design/icons';

import {
    devices as fleet,
    type FleetDevice,
    type PendingClaim,
    type ReleasePreview,
} from '../api/client';
import ProvisionDeviceDialog from '../components/ProvisionDeviceDialog';
import Value from '../components/Value';
import { useAuthStore } from '../state/authStore';
import { BRAND } from '../theme';
import { isStale, relative, timestamp } from '../lib/format';

const { Text, Paragraph } = Typography;

export default function Devices() {
    const session = useAuthStore((s) => s.session);
    const isAdmin = session?.role !== 'operator';
    // Taken from the App context rather than the static imports, so a confirm
    // dialog and a toast are drawn with this app's theme instead of the library
    // defaults.
    const { message, modal } = App.useApp();

    const [rows, setRows] = useState<FleetDevice[] | null>(null);
    const [pending, setPending] = useState<PendingClaim[]>([]);
    const [adding, setAdding] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [renaming, setRenaming] = useState<FleetDevice | null>(null);
    const [releasing, setReleasing] = useState<{ device: FleetDevice; preview: ReleasePreview } | null>(null);
    const [confirmText, setConfirmText] = useState('');

    const load = useCallback(async () => {
        if (!session) return;
        setRefreshing(true);
        try {
            // Fetched together so the two halves of the fleet cannot disagree: a claim
            // redeemed between two sequential calls would otherwise show as both a
            // pending code and a live device.
            const [list, open] = await Promise.all([
                fleet.list(session),
                // Operators can see the fleet but not provision it, and this endpoint
                // is admin-gated. Their 403 is not an error worth showing them.
                isAdmin ? fleet.pending(session) : Promise.resolve({ claims: [] as PendingClaim[] }),
            ]);
            setRows(list.devices);
            setPending(open.claims);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the fleet.');
        } finally {
            setRefreshing(false);
        }
    }, [session, isAdmin]);

    useEffect(() => {
        void load();
    }, [load]);

    async function act(id: string, fn: () => Promise<unknown>, done: string) {
        setBusyId(id);
        try {
            await fn();
            message.success(done);
            await load();
        } catch (err) {
            message.error(err instanceof Error ? err.message : 'That did not work.');
        } finally {
            setBusyId(null);
        }
    }

    async function openRelease(device: FleetDevice) {
        if (!session) return;
        setBusyId(device.id);
        try {
            // Previewed rather than described: whether the row survives depends on
            // data the operator cannot see, and telling them the wrong one is worse
            // than not telling them.
            const preview = await fleet.releasePreview(session, device.id);
            setConfirmText('');
            setReleasing({ device, preview });
        } catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not check that device.');
        } finally {
            setBusyId(null);
        }
    }

    function confirmDisable(d: FleetDevice) {
        modal.confirm({
            title: `Disable ${d.name ?? d.deviceId}?`,
            content:
                'It stops sending readings immediately. Its existing weighings are kept, and you can re-enable it at any time.',
            okText: 'Disable',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => act(d.id, () => fleet.disable(session!, d.id), 'Device disabled'),
        });
    }

    function cancelClaim(claim: PendingClaim) {
        modal.confirm({
            title: `Cancel ${claim.machineLabel}?`,
            content:
                'Its claim code stops working immediately. If the bridge has not been commissioned yet, add it again to get a new code.',
            okText: 'Cancel device',
            okButtonProps: { danger: true },
            cancelText: 'Keep it',
            onOk: () => act(claim.id, () => fleet.cancelClaim(session!, claim.id), 'Pending device cancelled'),
        });
    }

    const columns: ColumnsType<FleetDevice> = [
        {
            title: 'Device',
            key: 'device',
            render: (_, d) => (
                <Space direction="vertical" size={0}>
                    <Tooltip title={`Added ${timestamp(d.createdAt)}`}>
                        <Text strong>{d.name ?? d.deviceId}</Text>
                    </Tooltip>
                    {/* The id stays visible under a named device: it is what the
                        release dialog asks to be typed, and what the weighings
                        screen filters by. */}
                    {d.name && (
                        <Text type="secondary" className="mono" style={{ fontSize: 12 }}>
                            {d.deviceId}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            width: 150,
            render: (_, d) => <DeviceStatus device={d} />,
        },
        {
            title: 'Last reading',
            key: 'lastSeen',
            width: 170,
            render: (_, d) =>
                d.lastSeen ? (
                    <Tooltip title={timestamp(d.lastSeen)}>
                        <span>{relative(d.lastSeen)}</span>
                    </Tooltip>
                ) : (
                    <Value value={null} />
                ),
        },
        {
            title: 'Weighings',
            dataIndex: 'readingCount',
            key: 'readingCount',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.readingCount - b.readingCount,
            render: (n: number) => <span className="num">{n.toLocaleString('en-IN')}</span>,
        },
        {
            title: '',
            key: 'actions',
            width: 150,
            align: 'right',
            render: (_, d) => {
                const busy = busyId === d.id;
                return (
                    <Space size={4}>
                        {/* The shift-to-shift action, one click away. */}
                        {d.disabled ? (
                            <Button
                                size="small"
                                icon={<UndoOutlined />}
                                disabled={!isAdmin || busy}
                                onClick={() => act(d.id, () => fleet.restore(session!, d.id), 'Device re-enabled')}
                            >
                                Enable
                            </Button>
                        ) : (
                            <Tooltip title="Stops it sending readings. The hardware stays yours.">
                                <Button
                                    size="small"
                                    icon={<StopOutlined />}
                                    disabled={!isAdmin || busy}
                                    onClick={() => confirmDisable(d)}
                                >
                                    Disable
                                </Button>
                            </Tooltip>
                        )}

                        <Dropdown
                            trigger={['click']}
                            disabled={!isAdmin || busy}
                            menu={{
                                items: [
                                    {
                                        key: 'rename',
                                        icon: <EditOutlined />,
                                        label: 'Rename',
                                        onClick: () => setRenaming(d),
                                    },
                                    { type: 'divider' },
                                    {
                                        key: 'release',
                                        icon: <DisconnectOutlined />,
                                        danger: true,
                                        label: 'Release device',
                                        onClick: () => void openRelease(d),
                                    },
                                ],
                            }}
                        >
                            <Button
                                size="small"
                                type="text"
                                icon={<MoreOutlined />}
                                aria-label={`More actions for ${d.name ?? d.deviceId}`}
                                disabled={!isAdmin || busy}
                            />
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Card styles={{ body: { paddingTop: 16 } }}>
                <div className="sb-toolbar">
                    <div className="sb-toolbar__left">
                        <span className="sb-toolbar__count">
                            {rows === null
                                ? 'Loading the fleet…'
                                : `${rows.length} device${rows.length === 1 ? '' : 's'}${
                                      pending.length > 0 ? `, ${pending.length} waiting to be claimed` : ''
                                  }`}
                        </span>
                    </div>
                    <div className="sb-toolbar__right">
                        <Button icon={<ReloadOutlined />} loading={refreshing} onClick={() => void load()}>
                            Refresh
                        </Button>
                        <Tooltip title={isAdmin ? undefined : 'Only an admin can add a device.'}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={!isAdmin}
                                onClick={() => setAdding(true)}
                            >
                                Add device
                            </Button>
                        </Tooltip>
                    </div>
                </div>

                {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
                {!isAdmin && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="You can see the fleet but not change it"
                        description="Ask an admin to add, disable or release a device."
                    />
                )}

                <Table
                    rowKey="id"
                    size="middle"
                    columns={columns}
                    dataSource={rows ?? []}
                    loading={rows === null}
                    pagination={false}
                    scroll={{ x: 760 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary">No devices yet</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Add a device to get a claim code, then enter it on
                                            the bridge.
                                        </Text>
                                    </Space>
                                }
                            />
                        ),
                    }}
                />

                {/* Named and coded, but no hardware yet. Kept out of the table on
                    purpose: these have no device id, no last-seen and no weighing
                    count, so as rows they would be mostly empty columns. */}
                {pending.length > 0 && (
                    <div style={{ marginTop: 28 }}>
                        <span className="sb-section-label">Waiting to be claimed</span>
                        <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 12 }}>
                            {pending.map((c) => (
                                <PendingRow
                                    key={c.id}
                                    claim={c}
                                    busy={busyId === c.id}
                                    canManage={isAdmin}
                                    onCancel={() => cancelClaim(c)}
                                />
                            ))}
                        </Space>
                    </div>
                )}
            </Card>

            <ProvisionDeviceDialog
                open={adding}
                onClose={() => setAdding(false)}
                onCreated={() => void load()}
            />

            <RenameDialog
                device={renaming}
                busy={renaming !== null && busyId === renaming.id}
                onClose={() => setRenaming(null)}
                // Left open, with its Save button spinning, until the rename has
                // actually landed. Closing first would report success by
                // disappearing, before anything had been saved.
                onSave={async (name) => {
                    const d = renaming!;
                    await act(d.id, () => fleet.rename(session!, d.id, name), 'Renamed');
                    setRenaming(null);
                }}
            />

            <Modal
                open={releasing !== null}
                title={
                    <span>
                        <ApiOutlined style={{ marginRight: 8 }} />
                        Release {releasing?.device.name ?? releasing?.device.deviceId}
                    </span>
                }
                okText="Release this device"
                okButtonProps={{
                    danger: true,
                    // The confirmation has to match exactly. A misclick on a fleet
                    // list is otherwise indistinguishable from an intention, and this
                    // cannot be undone.
                    disabled: confirmText.trim() !== releasing?.preview.deviceId,
                }}
                onOk={() =>
                    act(
                        releasing!.device.id,
                        () => fleet.release(session!, releasing!.device.id, confirmText.trim()),
                        'Device released',
                    ).then(() => setReleasing(null))
                }
                onCancel={() => setReleasing(null)}
                destroyOnHidden
            >
                {releasing && (
                    <Space direction="vertical" size={14} style={{ width: '100%' }}>
                        <Paragraph style={{ marginBottom: 0 }}>
                            This gives the hardware up. It stops sending readings, leaves your fleet,
                            and the device id becomes free for another site to claim.
                        </Paragraph>

                        {/* The reassurance that matters, and it is not a promise -
                            the count comes from the server's own preview. */}
                        <Alert
                            type={releasing.preview.readingCount > 0 ? 'info' : 'warning'}
                            showIcon
                            message={
                                releasing.preview.readingCount > 0
                                    ? `Its ${releasing.preview.readingCount.toLocaleString()} weighing${
                                          releasing.preview.readingCount === 1 ? '' : 's'
                                      } are kept`
                                    : 'This device has never weighed anything'
                            }
                            description={
                                releasing.preview.readingCount > 0
                                    ? 'Nothing it has already recorded is deleted. Those weighings stay on your dashboard and their bill links keep working.'
                                    : 'There is nothing to keep, so its record is removed entirely.'
                            }
                        />

                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Type <Text code>{releasing.preview.deviceId}</Text> to confirm
                            </Text>
                            <Input
                                autoFocus
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder={releasing.preview.deviceId}
                                style={{ marginTop: 6 }}
                            />
                        </div>
                    </Space>
                )}
            </Modal>
        </>
    );
}

/**
 * What state a bridge is in, in one tag.
 *
 * Four states, not three. "Active" used to mean nothing more than "has reported
 * at least once in its life", so a bridge last heard from in March wore the same
 * green tag as one weighing right now — which is precisely the reading an
 * operator checking on a silent machine would take at face value. A device that
 * has gone quiet says so, using the same threshold the Overview screen uses, and
 * every tag carries an icon and a word as well as its colour.
 */
function DeviceStatus({ device }: { device: FleetDevice }) {
    if (device.disabled) {
        return (
            <Tooltip title="Its readings are refused by the receiver until it is re-enabled.">
                <Tag color="default" icon={<StopOutlined />}>
                    Disabled
                </Tag>
            </Tooltip>
        );
    }

    if (!device.lastSeen) {
        // "Never reported" is not the same as "off", and an operator chasing a
        // silent device needs to know which one it is.
        return (
            <Tooltip title="Claimed, but no weighing has ever arrived from it.">
                <Tag color="warning" icon={<HourglassOutlined />}>
                    Never reported
                </Tag>
            </Tooltip>
        );
    }

    if (isStale(device.lastSeen)) {
        return (
            <Tooltip
                title={`Nothing since ${timestamp(device.lastSeen)}. That may be a quiet shift rather than a fault.`}
            >
                <Tag color="default" icon={<ClockCircleOutlined />}>
                    Quiet
                </Tag>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={`Last weighing ${timestamp(device.lastSeen)}`}>
            <Tag color="success" icon={<ApiOutlined />}>
                Active
            </Tag>
        </Tooltip>
    );
}

/**
 * Rename a bridge.
 *
 * Its own dialog rather than an Input dropped into Modal.confirm's content. That
 * version wrote into a closure variable, so it had no validation, no way to
 * submit with the return key, and a Save button that stayed enabled when nothing
 * had been changed — a confirmed no-op that still cost a round trip.
 */
function RenameDialog({
    device,
    busy,
    onClose,
    onSave,
}: {
    device: FleetDevice | null;
    busy: boolean;
    onClose: () => void;
    onSave: (name: string | null) => void;
}) {
    const [name, setName] = useState('');

    useEffect(() => {
        if (device) setName(device.name ?? '');
    }, [device]);

    const trimmed = name.trim();
    const changed = trimmed !== (device?.name ?? '');

    function submit() {
        if (!changed) return;
        // An emptied field clears the name, which is what makes the device fall
        // back to showing its own id — so empty is a valid value, not a missing one.
        onSave(trimmed === '' ? null : trimmed);
    }

    return (
        <Modal
            open={device !== null}
            title="Rename device"
            okText="Save"
            okButtonProps={{ disabled: !changed, loading: busy }}
            onOk={submit}
            onCancel={onClose}
            destroyOnHidden
        >
            <Form layout="vertical" style={{ marginTop: 12 }}>
                <Form.Item
                    label="Device name"
                    help="Leave it empty to show the device id instead."
                >
                    <Input
                        autoFocus
                        value={name}
                        maxLength={64}
                        placeholder={device?.deviceId}
                        onChange={(e) => setName(e.target.value)}
                        onPressEnter={submit}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}

// ─── A device that has been named but has not presented itself ─────────
// The claim IS the record here; there is no device row yet, because the device id
// comes from the bridge's own MAC and nobody knows it until the hardware claims.
//
// The code is NOT shown, and cannot be: the server keeps only a hash. That is worth
// being explicit about, because an admin who lost the code will look for it here first.
function PendingRow({
    claim,
    busy,
    canManage,
    onCancel,
}: {
    claim: PendingClaim;
    busy: boolean;
    canManage: boolean;
    onCancel: () => void;
}) {
    return (
        <div className="sb-pending">
            <div className="sb-pending__icon" aria-hidden>
                <HourglassOutlined />
            </div>

            <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                <Text strong>{claim.machineLabel}</Text>
                <Space size={14} wrap>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <EnvironmentOutlined style={{ marginRight: 5, color: BRAND.muted }} />
                        {claim.location ?? <Text type="secondary" italic>Location not set</Text>}
                    </Text>
                    <Tooltip title={timestamp(claim.expiresAt)}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <ClockCircleOutlined style={{ marginRight: 5, color: BRAND.muted }} />
                            Code expires {relative(claim.expiresAt)}
                        </Text>
                    </Tooltip>
                </Space>
            </Space>

            <Tag color="warning" style={{ marginInlineEnd: 0 }}>
                Pending
            </Tag>
            {canManage && (
                <Button size="small" type="text" danger disabled={busy} onClick={onCancel}>
                    Cancel
                </Button>
            )}
        </div>
    );
}
